/**
 * AuthContext - UI State Only
 *
 * ⚠️ IMPORTANT: This context ONLY manages UI state (initialization, error display).
 * For actual authentication data, use hooks from @/hooks/useAuthQuery.ts
 *
 * Pattern:
 * - AuthContext: UI state (isInitialized, error)
 * - useSession(): React Query + Server Functions for auth data
 * - Server functions: getSessionServer, loginServer, logoutServer
 *
 * Migration from old pattern:
 * OLD: const { user } = useAuth() // Context fetching data ❌
 * NEW: const { data: user } = useSession() // React Query + Server Fn ✅
 */
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

export type AuthState = 'authenticated' | 'unauthenticated' | 'unavailable';

/**
 * UI State Interface
 * No data fetching, no user data - just UI coordination
 */
export interface AuthContextType {
  isInitialized: boolean;
  setIsInitialized: (value: boolean) => void;
  authState: AuthState | null;
  setAuthState: (value: AuthState) => void;
  error: string | null;
  setError: (error: string | null) => void;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * AuthProvider - Minimal UI State Management
 */
interface AuthProviderProps {
  children: ReactNode;
  initialAuthState?: AuthState;
}

export function AuthProvider({ children, initialAuthState }: AuthProviderProps) {
  const [isInitialized, setIsInitialized] = useState(initialAuthState !== undefined);
  const [authState, setAuthState] = useState<AuthState | null>(initialAuthState ?? null);
  const [rootAuthState, setRootAuthState] = useState(initialAuthState);
  const [error, setError] = useState<string | null>(null);

  // A root context change must reach descendants in the same render. Waiting
  // for an effect leaves one render where useSession can observe stale auth
  // state and start a cache-miss request after beforeLoad cleared the cache.
  if (initialAuthState !== rootAuthState) {
    setRootAuthState(initialAuthState);
    setAuthState(initialAuthState ?? null);
    setIsInitialized(initialAuthState !== undefined);
  }

  const clearError = useCallback(() => setError(null), []);

  // A fresh value object on every provider render re-renders every auth consumer
  // in the app, even when the auth state is unchanged (AuthProvider re-renders
  // whenever the root route context updates, i.e. on every navigation).
  const value = useMemo<AuthContextType>(
    () => ({
      isInitialized,
      setIsInitialized,
      authState,
      setAuthState,
      error,
      setError,
      clearError,
    }),
    [isInitialized, authState, error, clearError]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Hook to access auth UI state
 */
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/**
 * Convenience Hooks for Common Patterns
 */

export function useAuthInitialized(): boolean {
  const { isInitialized } = useAuth();
  return isInitialized;
}
